import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import { X, QuestionCircle } from 'react-bootstrap-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getAllUnidadesMedida } from '../../../../../../crud/configuration_api/unidades_medida';
import { getStorageUnitsByLocalId } from '../../../../../../crud/management_api/storage_units';
import { createIngredient } from '../../../../../../crud/menu_api/ingredients';
import { Dropdown } from 'react-bootstrap';
import IngredientStockConfiguration from '../IngredientStockConfiguration/IngredientStockConfiguration';
import IngredientCompositionManager from '../IngredientCompositionManager/IngredientCompositionManager';
import IngredientHelpModal from './components/HelpModal/IngredientHelpModal';
import './CreateIngredient.scss';
import { Ingredient, IngredientSelection as CompositionSelection } from '../../types';

interface ModalProps {
  show: boolean;
  handleClose: () => void;
  categoryId?: string;
  ingredients: any[];
  setIngredients: any;
  originalIngredients: Ingredient[];
  setIngredient: (data: any[]) => void;
  setOriginalIngredients: (data: Ingredient[]) => void; 
}

export interface Option {
  id: number;
  name: string;
}

export interface IngredientSelection {
  id: number;
  name: string;
  units: number;
  unidad_medida_id: number;
}

export const getUnidadesMedidaOptions = async () => {
  const response = await getAllUnidadesMedida();
  if (response) {
    const data = response.data;
    if (data) {
      const ums: Option[] = data.map((um: any) => ({
        id: um.unidad_medida_id,
        name: um.name + " - " + um.acronym,
      }));
      return ums;
    }
  }
  return [];
};

const CreateIngredient: React.FC<ModalProps> = ({
  show,
  handleClose,
  categoryId,
  ingredients,
  setIngredients,
  setIngredient,
  originalIngredients,
  setOriginalIngredients
}) => {
  const [isCombo, setIsCombo] = useState(false);
  const [isStock, setIsStock] = useState(false);
  const [unidadesMedida, setUnidadesMedida] = useState<Option[]>([]);
  const [storageUnits, setStorageUnits] = useState<any[]>([]);
  const [ingredientSelections, setIngredientSelections] = useState<IngredientSelection[]>([]);
  const [unidadMedidaId, setUnidadMedidaId] = useState(0);
  const [ingredientName, setIngredientName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedUnidadName, setSelectedUnidadName] = useState('Ninguno');
  const [isLoading, setIsLoading] = useState(false);

  // New state for enhanced ingredient management
  const [stockConfig, setStockConfig] = useState({
    units: '0',
    currentStock: '0',
    minQuantity: '0',
    selectedStorageUnit: null,
    unidadMedidaId: 0,
    selectedUnidadName: 'Ninguno',
  });
  const [compositionSelections, setCompositionSelections] = useState<CompositionSelection[]>([]);
  const [needProduction, setNeedProduction] = useState<boolean | null>(null);
  const [isTwin, setIsTwin] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  
  // Stock configuration helper functions
  const handleStockUnitsChange = (value: string) => {
    setStockConfig(prev => ({ ...prev, units: value }));
  };
  
  const handleCurrentStockChange = (value: string) => {
    setStockConfig(prev => ({ ...prev, currentStock: value }));
  };
  
  const handleMinQuantityChange = (value: string) => {
    setStockConfig(prev => ({ ...prev, minQuantity: value }));
  };
  
  const handleStorageUnitSelect = (unit: any) => {
    setStockConfig(prev => ({ 
      ...prev, 
      selectedStorageUnit: unit
    }));
  };

  // Cargar opciones de unidades de medida y storage units al montar el componente
  useEffect(() => {
    if (show) {
      const user = localStorage.getItem('user');
      const localId = user != null ? JSON.parse(user).local_id : 0;
      
      Promise.all([
        getUnidadesMedidaOptions(),
        getStorageUnitsByLocalId(localId)
      ]).then(([unidadesResponse, storageResponse]) => {
        if (unidadesResponse) {
          setUnidadesMedida(unidadesResponse);
          
          // Si la unidad de medida no está seleccionada, asignamos el valor por defecto
          if (unidadMedidaId === 0) {
            const defaultUnidad = unidadesResponse.find((um) => um.id === 5);
            if (defaultUnidad) {
              setUnidadMedidaId(defaultUnidad.id);
              setSelectedUnidadName(defaultUnidad.name);
            } else {
              setSelectedUnidadName("Ninguno");
            }
          }
        }
        
        if (storageResponse && storageResponse.data) {
          setStorageUnits(storageResponse.data);
        }
      }).catch((error) => {
        console.error('Error fetching data:', error);
        toast.error('Error al cargar las opciones');
      });
    }
  }, [show, unidadMedidaId]);

  // Este efecto se ejecuta solo cuando el modal se abre
  useEffect(() => {
    if (!show) {
      // Reiniciar a 'Ninguno' solo si no hay valor seleccionado previamente.
      if (unidadMedidaId === 0) {
        setSelectedUnidadName('Ninguno');
      }
    }
  }, [show, unidadMedidaId]);

  // Función para reiniciar los campos del formulario
  const resetForm = () => {
    setIngredientName('');
    setDescription('');
    setUnidadMedidaId(0);
    setSelectedUnidadName('Ninguno');
    setIsCombo(false);
    setIngredientSelections([]);
    setIsStock(false);
    setStockConfig({
      units: '0',
      currentStock: '0',
      minQuantity: '0',
      selectedStorageUnit: null,
      unidadMedidaId: 0,
      selectedUnidadName: 'Ninguno',
    });
    setCompositionSelections([]);
    setNeedProduction(null);
    setIsTwin(false);
  };

  // Si el modal se cierra, reiniciamos el formulario
  useEffect(() => {
    if (!show) {
      resetForm();
    }
  }, [show]);

  const handleFormSubmit = async () => {
    setIsLoading(true);
    const userContext = localStorage.getItem('user');
    const localId = userContext != null ? JSON.parse(userContext).local_id : 0;

    let formData = {
      ingredient_name: ingredientName,
      description: description,
      state: true,
      local_id: localId,
      unidad_medida_id: unidadMedidaId,
      ingredients_to_add: isCombo ? compositionSelections : ingredientSelections,
      stock_control: isStock,
      // New fields for enhanced ingredient management
      is_twin: isTwin,
      need_production: needProduction,
      has_ingredients: isCombo,
      ...(isStock && {
        min_quantity: parseFloat(stockConfig.minQuantity) || 0,
        unidad_medida_id: stockConfig.unidadMedidaId || unidadMedidaId,
        current_quantity: parseFloat(stockConfig.currentStock) || 0,
        storage_unit_id: (stockConfig.selectedStorageUnit as any)?.storage_unit_id || 0
      })
    };

    createIngredient(formData)
      .then((response) => {
        if (response) {
          toast.success('Ingrediente creado exitosamente');
          const updatedIngredient: Ingredient = (response as any).data;
          const newOriginalIngredients = [updatedIngredient, ...originalIngredients];
          setOriginalIngredients(newOriginalIngredients);
          setIngredients(newOriginalIngredients);
          setIngredient(newOriginalIngredients);
          handleClose();
        }
      })
      .catch((error) => {
        console.error("error ->", error);
        toast.error('Error al crear el item \n' + error.response?.data?.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderUnidadMedidaDropdown = () => {
    const handleSelect = (id: number, name: string) => {
      setUnidadMedidaId(id);
      setSelectedUnidadName(name);
    };

    return (
      <Form.Group controlId="unidad_medida_id" className='mp-formgroup vertical'>
        <Dropdown>
          <Dropdown.Toggle id="unidad-medida-dropdown" variant="none" className="unidad-medida-dropdown" >
            {selectedUnidadName}
          </Dropdown.Toggle>
          <Dropdown.Menu className='mp-dropdown-menu'>
            {unidadesMedida.map((um) => (
              <Dropdown.Item
                key={um.id}
                onClick={() => handleSelect(um.id, um.name)}
              >
                {um.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Form.Group>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} className='create-ingredient-modal-container'>
      <Modal.Header>
        <div className='form-modal-title-container'>
          <Modal.Title className='form-modal-title'>Crear Ingrediente</Modal.Title>
          <Badge 
            bg="info" 
            className="help-badge" 
            onClick={() => setShowHelpModal(true)}
            style={{ 
              cursor: 'pointer', 
              marginLeft: '10px',
              fontSize: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <QuestionCircle size={14} />
            ¿Cómo funciona?
          </Badge>
        </div>
        <Button variant="none" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col>
              <Form.Group controlId="ingredient_name" className='mp-formgroup vertical'>
                <Form.Control
                  type="text"
                  name="ingredient_name"
                  placeholder="Nombre"
                  value={ingredientName}
                  onChange={(e) => setIngredientName(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col>
              {renderUnidadMedidaDropdown()}
            </Col>
          </Row>

          <Form.Group controlId="description" className='mp-formgroup vertical'>
            <Form.Control
              type="text"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción"
            />
          </Form.Group>
          <div className="config-section">
            <div className="config-item">
              <div className="config-label">
                <FontAwesomeIcon icon={faLayerGroup} className="config-icon" />
                <div className="label-content">
                  <span className="label-title">Compuesto por otros sub-ingredientes</span>
                  <span className="label-subtitle">Este ingrediente está formado por otros ingredientes</span>
                </div>
              </div>
              <div className="config-toggle" onClick={() => setIsCombo(!isCombo)}>
                {isCombo ? (
                  <FaToggleOn className="toggle-icon active" />
                ) : (
                  <FaToggleOff className="toggle-icon inactive" />
                )}
                <span className={`toggle-status ${isCombo ? 'active' : 'inactive'}`}>
                  {isCombo ? 'Activado' : 'Desactivado'}
                </span>
              </div>
            </div>
          </div>
          
          {isCombo && (
            <IngredientCompositionManager
              ingredientSelections={compositionSelections}
              setIngredientSelections={setCompositionSelections}
            />
          )}

          <div className="config-section">
            <div className="config-item">
              <div className="config-label">
                <FontAwesomeIcon icon={faWarehouse} className="config-icon" />
                <div className="label-content">
                  <span className="label-title">Activar control de stock</span>
                  <span className="label-subtitle">Habilitar seguimiento de inventario para este ingrediente</span>
                </div>
              </div>
              <div className="config-toggle" onClick={() => setIsStock(!isStock)}>
                {isStock ? (
                  <FaToggleOn className="toggle-icon active" />
                ) : (
                  <FaToggleOff className="toggle-icon inactive" />
                )}
                <span className={`toggle-status ${isStock ? 'active' : 'inactive'}`}>
                  {isStock ? 'Activado' : 'Desactivado'}
                </span>
              </div>
            </div>
          </div>

          {isStock && (
            <IngredientStockConfiguration
              isStock={isStock}
              setIsStock={setIsStock}
              unidadesMedida={unidadesMedida}
              unidadMedidaId={stockConfig.unidadMedidaId}
              selectedUnidadName={stockConfig.selectedUnidadName}
              onUnidadMedidaSelect={(id: number, name: string) => {
                setStockConfig(prev => ({ ...prev, unidadMedidaId: id, selectedUnidadName: name }));
              }}
              units={stockConfig.units}
              setUnits={handleStockUnitsChange}
              currentStock={stockConfig.currentStock}
              setCurrentStock={handleCurrentStockChange}
              minQuantity={stockConfig.minQuantity}
              setMinQuantity={handleMinQuantityChange}
              storageUnits={storageUnits}
              selectedStorageUnit={stockConfig.selectedStorageUnit}
              onStorageUnitSelect={handleStorageUnitSelect}
              needProduction={needProduction}
              setNeedProduction={setNeedProduction}
              hasIngredients={isCombo}
              view="unitsOnly"
              mainUnidadMedidaId={unidadMedidaId}
            />
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button className="form-button-cancel" variant="none" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            className="form-button-create"
            variant="none"
            onClick={handleFormSubmit}
            disabled={
              isLoading || 
              !ingredientName || 
              !unidadMedidaId || 
              (isStock && compositionSelections.length > 0 && needProduction === null)
            }
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Creando...
              </>
            ) : (
              'Crear'
            )}
          </Button>
        </div>
      </Modal.Footer>

      {/* Help Modal */}
      <IngredientHelpModal 
        show={showHelpModal} 
        handleClose={() => setShowHelpModal(false)} 
      />
    </Modal>
  );
};

export default CreateIngredient;
